/**
 * Máquina de estados da Ordem de Serviço.
 *
 * Os 10 status vieram do protótipo. O protótipo permitia qualquer transição;
 * aqui as transições são explícitas. Voltar etapas e cancelar exigem observação
 * (fica gravada no histórico) e algumas reaberturas são exclusivas do administrador.
 */
import type { Role } from './roles';

export const ORDER_STATUSES = [
  'AGUARDANDO',
  'DIAGNOSTICO',
  'AGUARDANDO_APROVACAO',
  'APROVADO',
  'REPARO',
  'AGUARDANDO_PECA',
  'CONCLUIDO',
  'ENTREGUE',
  'CANCELADO',
  'NAO_APROVADO',
  'SEM_REPARO',
] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];

/** Nome do status (como no protótipo). */
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  AGUARDANDO: 'Aberto',
  DIAGNOSTICO: 'Diagnóstico',
  AGUARDANDO_APROVACAO: 'Aguardando Aprovação',
  APROVADO: 'Aprovado',
  REPARO: 'Reparo',
  AGUARDANDO_PECA: 'Aguardando Peça',
  CONCLUIDO: 'Concluído',
  ENTREGUE: 'Entregue',
  CANCELADO: 'Cancelado',
  NAO_APROVADO: 'Orçamento não aprovado',
  SEM_REPARO: 'Sem reparo',
};

/** Descrição curta usada no diálogo de status e no PDF do protótipo (função `statusText`). */
export const ORDER_STATUS_DESCRIPTIONS: Record<OrderStatus, string> = {
  AGUARDANDO: 'OS aberta',
  DIAGNOSTICO: 'Em análise',
  AGUARDANDO_APROVACAO: 'Orçamento enviado',
  APROVADO: 'Orçamento aprovado',
  REPARO: 'Em reparo',
  AGUARDANDO_PECA: 'Aguardando peça',
  CONCLUIDO: 'Pronta / testada',
  ENTREGUE: 'Entregue ao cliente',
  CANCELADO: 'Cancelada',
  NAO_APROVADO: 'Cliente recusou o orçamento',
  SEM_REPARO: 'Equipamento não pode ser reparado',
};

/** OS "abertas" = em andamento (regra do protótipo). */
export const OPEN_STATUSES: readonly OrderStatus[] = [
  'AGUARDANDO',
  'DIAGNOSTICO',
  'AGUARDANDO_APROVACAO',
  'APROVADO',
  'REPARO',
  'AGUARDANDO_PECA',
];

/**
 * Status que o filtro "Ocultar finalizadas" pode esconder: os que significam que a OS
 * já foi encerrada com o cliente — entregue, cancelado, orçamento não aprovado e sem reparo.
 *
 * "Concluído" NÃO entra aqui: a OS está pronta mas ainda não foi entregue ao cliente,
 * então continua aparecendo na listagem mesmo com o filtro ligado.
 */
export const COMPLETED_STATUSES: readonly OrderStatus[] = ['ENTREGUE', 'CANCELADO', 'NAO_APROVADO', 'SEM_REPARO'];

/**
 * Status que o filtro "Ocultar finalizadas" esconde SEMPRE, mesmo com pendência financeira.
 * OS cancelada, não aprovada ou sem reparo não gera cobrança, então um saldo "em aberto" nelas não é dívida real.
 */
export const ALWAYS_HIDDEN_STATUSES: readonly OrderStatus[] = ['CANCELADO', 'NAO_APROVADO', 'SEM_REPARO'];

/** Status em que a OS fica bloqueada para edição de dados e itens. */
export const LOCKED_STATUSES: readonly OrderStatus[] = ['ENTREGUE', 'CANCELADO', 'NAO_APROVADO', 'SEM_REPARO'];

/** Status finais em que a OS não gera cobrança nem consome estoque. */
export const NO_CHARGE_STATUSES: readonly OrderStatus[] = ['CANCELADO', 'NAO_APROVADO', 'SEM_REPARO'];

/** Status finais que encerram a contagem de dias na assistência. */
export const CLOSED_STATUSES: readonly OrderStatus[] = ['ENTREGUE', 'CANCELADO', 'NAO_APROVADO', 'SEM_REPARO'];

export interface StatusTransition {
  from: OrderStatus;
  to: OrderStatus;
  /** Exige observação (motivo) — voltar etapa, cancelar, reabrir. */
  requiresNote: boolean;
  /** Só administradores podem executar. */
  adminOnly: boolean;
}

const t = (from: OrderStatus, to: OrderStatus, requiresNote = false, adminOnly = false): StatusTransition => ({
  from,
  to,
  requiresNote,
  adminOnly,
});

export const STATUS_TRANSITIONS: readonly StatusTransition[] = [
  // Entrada
  t('AGUARDANDO', 'DIAGNOSTICO'),
  t('AGUARDANDO', 'CANCELADO', true),
  // Diagnóstico
  t('DIAGNOSTICO', 'AGUARDANDO_APROVACAO'),
  t('DIAGNOSTICO', 'REPARO'), // reparo pré-autorizado pelo cliente
  t('DIAGNOSTICO', 'AGUARDANDO_PECA'), // peça precisa chegar para concluir o diagnóstico
  t('DIAGNOSTICO', 'AGUARDANDO', true),
  t('DIAGNOSTICO', 'CANCELADO', true),
  t('DIAGNOSTICO', 'NAO_APROVADO'),
  t('DIAGNOSTICO', 'SEM_REPARO'),
  // Orçamento
  t('AGUARDANDO_APROVACAO', 'APROVADO'),
  t('AGUARDANDO_APROVACAO', 'DIAGNOSTICO', true),
  t('AGUARDANDO_APROVACAO', 'NAO_APROVADO'), // orçamento recusado pelo cliente
  t('AGUARDANDO_APROVACAO', 'CANCELADO', true),
  // Aprovado
  t('APROVADO', 'REPARO'),
  t('APROVADO', 'AGUARDANDO_PECA'),
  t('APROVADO', 'CANCELADO', true),
  // Reparo
  t('REPARO', 'AGUARDANDO_PECA'),
  t('REPARO', 'CONCLUIDO'),
  t('REPARO', 'AGUARDANDO_APROVACAO'), // novo orçamento durante o reparo
  t('REPARO', 'SEM_REPARO'),
  t('REPARO', 'CANCELADO', true),
  // Peça
  t('AGUARDANDO_PECA', 'REPARO'),
  t('AGUARDANDO_PECA', 'DIAGNOSTICO'),
  t('AGUARDANDO_PECA', 'CANCELADO', true),
  // Pronta
  t('CONCLUIDO', 'ENTREGUE'),
  t('CONCLUIDO', 'REPARO', true), // falhou no teste / reabertura
  // Reaberturas administrativas
  t('ENTREGUE', 'CONCLUIDO', true, true),
  t('CANCELADO', 'AGUARDANDO', true, true),
  t('NAO_APROVADO', 'AGUARDANDO_APROVACAO', true, true),
  t('SEM_REPARO', 'DIAGNOSTICO', true, true),
];

export function findTransition(from: OrderStatus, to: OrderStatus): StatusTransition | undefined {
  return STATUS_TRANSITIONS.find((x) => x.from === from && x.to === to);
}

export function allowedTransitions(from: OrderStatus, role?: Role): StatusTransition[] {
  return STATUS_TRANSITIONS.filter((x) => x.from === from && (!x.adminOnly || role === 'ADMIN'));
}

export type TransitionCheck =
  | { ok: true; transition: StatusTransition }
  | { ok: false; reason: string };

export function checkTransition(
  from: OrderStatus,
  to: OrderStatus,
  role: Role,
  note?: string | null,
): TransitionCheck {
  if (from === to) return { ok: false, reason: 'A OS já está neste status.' };
  const transition = findTransition(from, to);
  if (!transition) {
    return {
      ok: false,
      reason: `Não é possível passar de "${ORDER_STATUS_LABELS[from]}" para "${ORDER_STATUS_LABELS[to]}".`,
    };
  }
  if (transition.adminOnly && role !== 'ADMIN') {
    return { ok: false, reason: 'Somente administradores podem reabrir esta OS.' };
  }
  if (transition.requiresNote && !note?.trim()) {
    return { ok: false, reason: 'Informe uma observação explicando o motivo da alteração.' };
  }
  return { ok: true, transition };
}

export const isOpenStatus = (s: OrderStatus) => OPEN_STATUSES.includes(s);
export const isLockedStatus = (s: OrderStatus) => LOCKED_STATUSES.includes(s);
